import React, { FC, ReactNode, useContext, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { AppContext } from 'contexts/AppProvider'
import Footer, { Menu } from 'components/buffet/Footer'
import ModalProductModifiers from 'components/modals/ModalProductModifiers'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import { useSelector } from 'react-redux'
import { buffetSelector, closeModalProductModifier } from 'store/slices/buffetSlice'
import { ThemeColor } from 'types/color'
import { addProductToPrivateCart, updatePrivateCartItem } from 'store/slices/privateCartSlice'
import BuffetModals from './BuffetModals'
import Screen from './BuffetOverlayPage'
import { ProductServiceEnum } from 'graphQL/dinein/generate/operations'
import { useHistory } from 'react-router-dom'
import { SubPageName } from 'enums/buffet'
import globalStyles from 'styles/global-styles'
import { LANGUAGES } from 'helpers/language'
import { MODE_MODALCUSTOMERNOTES } from 'helpers/cart'
import useServiceType from 'hooks/buffet/useServiceType'
import AlertLabelBox from 'components/base-ui/AlertLabelBox'
import useSearchParams from 'hooks/useSearchParams'

interface Props {
  children: ReactNode
  activeFooter?: boolean
  currentMenu?: 'Menu' | 'Submitted Order' | ''
}

const BuffetContainer: FC<Props> = ({ children, activeFooter = false, currentMenu = '' }) => {
  const { brand } = useContext(AppContext)
  const buffet = useSelector(buffetSelector)
  const { serviceType } = useServiceType()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const [addToCartProcessing, setAddToCartProcessing] = useState(false)
  const { params } = useSearchParams()
  const productServiceType = params.get('product_service_type') || undefined

  const {
    t,
    i18n: { language },
  } = useTranslation()

  const buffetMenu: Menu[] = [
    {
      testId: 'menu',
      name: t('buffet.footer.menu'),
      icon: 'tray',
      active: currentMenu === 'Menu',
      path: SubPageName.Home,
    },
    {
      testId: 'globalCart',
      name: t('buffet.footer.order'),
      icon: 'board-check',
      active: currentMenu === 'Submitted Order',
      path: SubPageName.GlobalCart,
    },
  ]

  return (
    <Wrapper>
      <GlobalStyle />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/gh/fancyapps/fancybox@3.5.7/dist/jquery.fancybox.min.css" />
      {children}
      <Screen screenType={buffet.screen.type} isProcessing={buffet.screen.isProcessing} />
      <BuffetModals />
      <AlertLabelBox {...buffet.alertLabelBox} />
      <ModalProductModifiers
        data-testid="modal-product-modifier"
        lang={language as LANGUAGES}
        isOpen={buffet.modalProductModifier.isOpen}
        isLoading={buffet.modalProductModifier.isLoading}
        onRequestClose={() => {
          if (!addToCartProcessing) dispatch(closeModalProductModifier())
        }}
        productData={{ ...buffet.productSelected.item, ...buffet.modifiersSelected }}
        defaultQuantityValue={buffet.productSelected.quantity}
        defaultValue={buffet.productSelected.customNotes}
        defaultValueModifiers={buffet.modifiersSelected}
        onUpdateCustomNotes={(product) => dispatch(updatePrivateCartItem(product))}
        onAddToCart={(products) => {
          setAddToCartProcessing(true)
          products.forEach((product) => {
            dispatch(addProductToPrivateCart({ ...product, serviceType: serviceType || productServiceType }))
          })
          setAddToCartProcessing(false)
        }}
        addToCartProcessing={addToCartProcessing}
        disabledAddTocartBtn={addToCartProcessing || !buffet.modalProductModifier.isOpen}
        mode={buffet.modalProductModifier.mode as MODE_MODALCUSTOMERNOTES}
        isShowIconWithButton
        hideZeroPrice
        showCustomNoteSkeleton={!buffet.modalProductModifier.skipCustomNotesScreen}
        quantitySwitchColor={ThemeColor.primary}
        warningTitle={
          buffet.productSelected.item.availableServices?.includes(ProductServiceEnum.BuffetALaCarte)
            ? t('buffet.warning.alacarte')
            : ''
        }
        titleJustify={
          buffet.productSelected.item.availableServices?.includes(ProductServiceEnum.BuffetALaCarte)
            ? 'space-between'
            : 'center'
        }
        productPriceColor="#FF4848"
      />
      {brand?.id && activeFooter && (
        <FooterWrapper>
          <Footer menuList={buffetMenu} onClickMenu={({ path }) => history.push(path)} />
        </FooterWrapper>
      )}
    </Wrapper>
  )
}

export default BuffetContainer

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
  background: ${({ theme }) => theme.colors.white};
`

const FooterWrapper = styled.div`
  z-index: 99;
  position: fixed;
  right: 0;
  left: 0;
  bottom: 0;
`

const GlobalStyle = createGlobalStyle`
  ${globalStyles}
`
